import { EventBus } from '../events/EventBus';

export type TournamentStatsDelta = {
  busts?: number;
  skeechCount?: number;
  highestTurnScore?: number;
  longestNoDitchStreak?: number;
  totalPoints?: number;
  totalRolls?: number;
  // Placeholders for future physics-derived stats
  popUps?: number;
  orbits?: number;
  fastestRoll?: number; // m/s
  slowestRoll?: number; // m/s (min non-zero)
  achievements?: string[]; // Unlocked achievement IDs for this tournament
};

export class AchievementsTracker {
  private currentNoDitchStreak = 0;
  private maxNoDitchStreak = 0;
  private busts = 0;
  private skeechCount = 0;
  private highestTurnScore = 0;
  private totalPoints = 0;
  private totalRolls = 0;
  private fastestRoll: number | undefined = undefined;
  private slowestRoll: number | undefined = undefined; // min positive
  private maxOrbits: number = 0; // max orbits achieved in a single roll
  private popUps: number = 0;   // total back-edge pops across tournament games (sum)

  constructor(private bus: EventBus) {
    this.attach();
  }

  reset() {
    this.currentNoDitchStreak = 0;
    this.maxNoDitchStreak = 0;
    this.busts = 0;
    this.skeechCount = 0;
    this.highestTurnScore = 0;
    this.totalPoints = 0;
    this.totalRolls = 0;
    this.fastestRoll = undefined;
    this.slowestRoll = undefined;
    this.maxOrbits = 0;
    this.popUps = 0;
  }

  private attach() {
    // Accept optional speed/orbits without relying on a strict typed payload
    this.bus.on('roll_completed', (payload: any) => {
      const points: number = payload?.points ?? 0;
      const result: 'ditch' | 'hole' = payload?.result;
      const speed: number | undefined = typeof payload?.speed === 'number' ? payload.speed : undefined;
      const orbits: number | undefined = typeof payload?.orbits === 'number' ? payload.orbits : undefined;
      const popUps: number | undefined = typeof payload?.popUps === 'number' ? payload.popUps : undefined;
      this.totalRolls += 1;
      this.totalPoints += points;
      if (result === 'ditch' || points === 0) {
        this.currentNoDitchStreak = 0;
      } else {
        this.currentNoDitchStreak += 1;
        if (this.currentNoDitchStreak > this.maxNoDitchStreak) this.maxNoDitchStreak = this.currentNoDitchStreak;
      }

      // Track fastest/slowest roll speeds when available
      if (typeof speed === 'number' && speed > 0) {
        if (this.fastestRoll === undefined || speed > this.fastestRoll) this.fastestRoll = speed;
        if (this.slowestRoll === undefined || speed < this.slowestRoll) this.slowestRoll = speed;
      }

      // Track max orbits when available
      if (typeof orbits === 'number' && orbits >= 0) {
        if (orbits > this.maxOrbits) this.maxOrbits = orbits;
      }

      if (typeof popUps === 'number' && popUps > 0) {
        this.popUps += popUps;
      }
    });

    this.bus.on('bust', () => { this.busts += 1; });
    this.bus.on('skeech', () => { this.skeechCount += 1; });
    this.bus.on('turn_completed', ({ points }) => {
      if (points > this.highestTurnScore) this.highestTurnScore = points;
    });
  }

  getTournamentDelta(): TournamentStatsDelta {
    return {
      busts: this.busts,
      skeechCount: this.skeechCount,
      highestTurnScore: this.highestTurnScore,
      longestNoDitchStreak: this.maxNoDitchStreak,
      totalPoints: this.totalPoints,
      totalRolls: this.totalRolls,
      fastestRoll: this.fastestRoll,
      slowestRoll: this.slowestRoll,
      orbits: this.maxOrbits,
      popUps: this.popUps,
      achievements: this.getUnlockedAchievements(),
    };
  }

  getUnlockedAchievements(): string[] {
    const out: string[] = [];
    if (this.skeechCount > 0) out.push('skroll');
    if (this.maxNoDitchStreak >= 10) out.push('no_ditch_10');
    // Apollo13 pending physics signal
    return out;
  }
}
